import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  Select,
  Card,
  Space,
  Divider,
  message,
  Tag,
  Popconfirm
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import apiClient from '../services/axiosConfig';
import { useTheme } from '../App';

const { Option } = Select;

const ValidationRuleManager = ({ apiConfigId, visible, onClose, apiConfig }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form] = Form.useForm();
  
  // Use theme
  const { theme } = useTheme();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (visible && apiConfigId) {
      fetchRules();
    }
  }, [visible, apiConfigId]);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${API_BASE_URL}/validation-rules/api/${apiConfigId}`);
      setRules(response.data);
    } catch (error) {
      message.error('Failed to fetch validation rules');
    }
    setLoading(false);
  };

  const handleSubmit = async (values) => {
    try {
      console.log('Form values before transform:', values);
      
      const transformedValues = {
        ...values,
        fields: values.fields?.map(fieldPath => {
          const option = getParameterOptions().find(opt => opt.value === fieldPath);
          return {
            path: fieldPath,
            parameterType: option?.parameterType || fieldPath.split('.')[0],
            fieldName: option?.fieldName || fieldPath.split('.').pop(),
            parentPath: option?.parentPath || undefined
          };
        }) || []
      };

      console.log('Transformed values:', transformedValues);

      if (editingRule) {
        await apiClient.put(`${API_BASE_URL}/validation-rules/${editingRule._id}`, transformedValues);
        message.success('Validation rule updated successfully');
      } else {
        await apiClient.post(`${API_BASE_URL}/validation-rules`, {
          ...transformedValues,
          apiConfigId
        });
        message.success('Validation rule created successfully');
      }
      
      fetchRules();
      setFormVisible(false);
      setEditingRule(null);
      form.resetFields();
    } catch (error) {
      console.error('Validation rule error:', error.response?.data || error.message);
      message.error('Failed to save validation rule: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (ruleId) => {
    try {
      await apiClient.delete(`${API_BASE_URL}/validation-rules/${ruleId}`);
      message.success('Validation rule deleted successfully');
      fetchRules();
    } catch (error) {
      message.error('Failed to delete validation rule');
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    
    const formValues = {
      ...rule,
      fields: rule.fields?.map(field => field.path) || []
    };
    
    console.log('Setting form values for edit:', formValues);
    form.setFieldsValue(formValues);
    setFormVisible(true);
  };

  const getParameterOptions = () => {
    if (!apiConfig || !apiConfig.parameters) return [];
    
    const options = [];
    
    apiConfig.parameters.forEach(param => {
      options.push({
        label: `${param.parameterType}.${param.name}`,
        value: `${param.parameterType}.${param.name}`,
        parameterType: param.parameterType,
        fieldName: param.name
      });
      
      if (param.dataType === 'object' && param.objectSchema) {
        param.objectSchema.forEach(field => {
          options.push({
            label: `${param.parameterType}.${param.name}.${field.fieldName}`,
            value: `${param.parameterType}.${param.name}.${field.fieldName}`,
            parameterType: param.parameterType,
            fieldName: field.fieldName,
            parentPath: param.name
          });
          
          if (field.fieldType === 'object' && field.objectSchema) {
            field.objectSchema.forEach(nestedField => {
              options.push({
                label: `${param.parameterType}.${param.name}.${field.fieldName}.${nestedField.fieldName}`,
                value: `${param.parameterType}.${param.name}.${field.fieldName}.${nestedField.fieldName}`,
                parameterType: param.parameterType,
                fieldName: nestedField.fieldName,
                parentPath: `${param.name}.${field.fieldName}`
              });
            });
          }
        });
      }
      
      if (param.dataType === 'array' && param.arrayItemType === 'object' && param.objectSchema) {
        param.objectSchema.forEach(field => {
          options.push({
            label: `${param.parameterType}.${param.name}.${field.fieldName}`,
            value: `${param.parameterType}.${param.name}.${field.fieldName}`,
            parameterType: param.parameterType,
            fieldName: field.fieldName,
            parentPath: param.name
          });
        });
      }
    });
    
    return options;
  };

  const columns = [
    {
      title: 'Rule ID',
      dataIndex: 'ruleId',
      key: 'ruleId',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={
          type === 'oneOf' ? 'blue' :
          type === 'conditional' ? 'orange' :
          type === 'mutuallyExclusive' ? 'red' : 'green'
        }>
          {type}
        </Tag>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Fields',
      dataIndex: 'fields',
      key: 'fields',
      render: (fields) => (
        <div>
          {fields.map((field, index) => (
            <Tag key={index} style={{ marginBottom: 4 }}>
              {field.path}
            </Tag>
          ))}
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this rule?"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Modal
      title={<span style={{ color: theme.text }}>Validation Rules - {apiConfig?.name}</span>}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      bodyStyle={{ backgroundColor: theme.surface }}
    >
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingRule(null);
            form.resetFields();
            setFormVisible(true);
          }}
        >
          Add Validation Rule
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={rules}
        rowKey="_id"
        loading={loading}
        pagination={false}
      />

      {/* Form Modal */}
      <Modal
        title={editingRule ? 'Edit Validation Rule' : 'Add Validation Rule'}
        visible={formVisible}
        onCancel={() => {
          setFormVisible(false);
          setEditingRule(null);
          form.resetFields();
        }}
        footer={null}
        width={800}
        bodyStyle={{ backgroundColor: theme.surface }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="ruleId"
            label="Rule ID"
            rules={[{ required: true, message: 'Please enter rule ID' }]}
          >
            <Input placeholder="e.g., contact-method" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Rule Type"
            rules={[{ required: true, message: 'Please select rule type' }]}
          >
            <Select>
              <Option value="oneOf">One Of (Either A OR B required)</Option>
              <Option value="conditional">Conditional (A required IF B equals X)</Option>
              <Option value="mutuallyExclusive">Mutually Exclusive (A and B cannot both exist)</Option>
              <Option value="allRequired">All Required (A AND B AND C together)</Option>
              <Option value="dependsOn">Depends On (A required IF B exists)</Option>
              <Option value="requiredGroup">Required Group (At least N from group)</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <Input placeholder="Human readable description of this rule" />
          </Form.Item>

          <Form.Item
            name="errorMessage"
            label="Error Message"
          >
            <Input placeholder="Custom error message (optional)" />
          </Form.Item>

          <Form.Item
            name="fields"
            label="Fields"
            rules={[{ required: true, message: 'Please select fields' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select fields involved in this rule"
              optionLabelProp="label"
            >
              {getParameterOptions().map(option => (
                <Option
                  key={option.value}
                  value={option.value}
                  label={option.label}
                >
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.type !== currentValues.type
            }
            noStyle
          >
            {({ getFieldValue }) => {
              const ruleType = getFieldValue('type');
              if (ruleType === 'conditional' || ruleType === 'dependsOn') {
                return (
                  <>
                    <Divider>Condition Settings</Divider>
                    <Form.Item
                      name={['condition', 'field']}
                      label="Condition Field"
                      rules={[{ required: true, message: 'Please select condition field' }]}
                    >
                      <Select placeholder="Field to check condition against">
                        {getParameterOptions().map(option => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name={['condition', 'operator']}
                      label="Operator"
                      rules={[{ required: true, message: 'Please select operator' }]}
                    >
                      <Select>
                        <Option value="equals">Equals</Option>
                        <Option value="notEquals">Not Equals</Option>
                        <Option value="exists">Exists</Option>
                        <Option value="notExists">Not Exists</Option>
                        <Option value="in">In Array</Option>
                        <Option value="notIn">Not In Array</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name={['condition', 'value']}
                      label="Value"
                      rules={[{ required: true, message: 'Please enter value' }]}
                    >
                      <Input placeholder="Value to compare against" />
                    </Form.Item>
                  </>
                );
              }
              return null;
            }}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
              <Button onClick={() => {
                setFormVisible(false);
                setEditingRule(null);
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};

export default ValidationRuleManager;